import React from 'react';
import { Link } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { AITool } from '../types';

interface ToolFeatureComparisonProps {
  currentTool: AITool;
  competitors: AITool[];
}

export const ToolFeatureComparison: React.FC<ToolFeatureComparisonProps> = ({ 
  currentTool, 
  competitors 
}) => {
  // Define features to compare
  const features = [
    { 
      name: 'Free Plan', 
      check: (tool: AITool) => tool.pricingType === 'free' || tool.pricingType === 'freemium'
    },
    { 
      name: 'Beginner Friendly', 
      check: (tool: AITool) => tool.experienceLevels.includes('beginner')
    },
    { 
      name: 'Advanced Features', 
      check: (tool: AITool) => tool.experienceLevels.includes('advanced')
    },
    { 
      name: 'Writing Capabilities', 
      check: (tool: AITool) => tool.useCases.includes('writing')
    },
    { 
      name: 'Image Generation', 
      check: (tool: AITool) => tool.useCases.includes('images')
    },
    { 
      name: 'Data Analysis', 
      check: (tool: AITool) => tool.useCases.includes('data-analysis')
    }
  ];

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Feature Comparison</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Feature
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-indigo-50 dark:bg-indigo-900/20">
                  {currentTool.name}
                </th>
                {competitors.map(competitor => (
                  <th key={competitor.id} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {competitor.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {features.map((feature, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-900/20' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {feature.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-900/20">
                    {feature.check(currentTool) ? (
                      <Check className="text-green-500" size={20} />
                    ) : (
                      <X className="text-red-500" size={20} />
                    )}
                  </td>
                  {competitors.map(competitor => (
                    <td key={competitor.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {feature.check(competitor) ? (
                        <Check className="text-green-500" size={20} />
                      ) : (
                        <X className="text-red-500" size={20} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Compatibility Score Row */}
              <tr className="bg-gray-100 dark:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  Compatibility Score
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20">
                  {currentTool.compatibilityScore}%
                </td>
                {competitors.map(competitor => (
                  <td key={competitor.id} className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 dark:text-gray-100">
                    {competitor.compatibilityScore}%
                  </td>
                ))}
              </tr>
              {/* Link Row */}
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  Details
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 bg-indigo-50 dark:bg-indigo-900/20">
                  <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                    Current Tool
                  </span>
                </td>
                {competitors.map(competitor => (
                  <td key={competitor.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <Link 
                      to={`/tool/${competitor.id}`}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 font-medium"
                    >
                      View Details
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};